import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectMarketplaceDetailed } from "../../store/auth-slice";
import {
  useConsultOrderContext,
  ConsultOrderFulfillment,
  ConsultOrderAddressMode,
  DeliveryServiceFee,
} from "../../contexts/ConsultOrderContext";
import {
  CncOrderDeliveryType,
  PersonalIdType,
  DeliveryTimeSlotsResponse,
  BranchServiceItem,
} from "../../types/consultOrder.types";
import {
  ConsultOrderValidationErrors,
  deliveryZoneKey,
} from "../../utils/consultOrderPayload";
import { resolveServiceFee } from "../../utils/consultServiceFee";
import {
  checkDeliveryDistance,
  checkDeliveryTimeSlots,
  getBranchServiceItems,
} from "../../services/consultOrderService";
import AddressCreate from "./AddressCreate";
import styles from "./DeliverySection.module.scss";

// ITAM-2583 — the delivery/pickup half of the consult-order form. Fully
// context-driven (reads/writes the shared draft); the only prop is the
// field-linked validation error map. Delivery pre-checks (zone + time slots)
// go through the NON-intercepted service calls so an expected VC-2 403 renders
// a graceful notice instead of logging the pharmacist out.

const FULFILLMENT_OPTIONS: { value: ConsultOrderFulfillment; label: string }[] =
  [
    { value: "PICKUP", label: "Lično preuzimanje" },
    { value: "STANDARD_DELIVERY", label: "Standardna dostava" },
    { value: "FAST_DELIVERY", label: "Brza dostava" },
  ];

const PERSONAL_ID_TYPE_OPTIONS: { value: PersonalIdType; label: string }[] = [
  { value: "JMBG", label: "JMBG" },
  { value: "PERSONAL_CARD_NUMBER", label: "Broj lične karte" },
  { value: "PASSPORT_NUMBER", label: "Broj pasoša" },
];

const slotOptionValue = (start: string, end: string) => `${start}|${end}`;

interface DeliverySectionProps {
  errors?: ConsultOrderValidationErrors;
}

const DeliverySection: React.FC<DeliverySectionProps> = ({ errors }) => {
  const { draft, updateDraft, prefill } = useConsultOrderContext();
  const detailed = useSelector(selectMarketplaceDetailed);

  const isDelivery = draft.fulfillment !== "PICKUP";

  const marketplaceLat = detailed?.latitude ?? null;
  const marketplaceLng = detailed?.longitude ?? null;
  const marketplaceId = detailed?.marketplaceId ?? null;
  const branchMarketplaceId = detailed?.branchMarketplaceId ?? null;

  // Branch SERVICE items = the delivery-fee source (the nullable
  // detailed.deliveryPrice* is NOT trusted). Fetched once per branch while the
  // delivery UI is relevant; the fee/serviceItemId are resolved by exact name.
  const [serviceItems, setServiceItems] = useState<BranchServiceItem[] | null>(
    null
  );
  const [serviceLoading, setServiceLoading] = useState(false);
  const [serviceError, setServiceError] = useState(false);

  useEffect(() => {
    if (!isDelivery || marketplaceId == null || branchMarketplaceId == null) {
      return;
    }
    const controller = new AbortController();
    setServiceLoading(true);
    setServiceError(false);
    getBranchServiceItems(marketplaceId, branchMarketplaceId, controller.signal)
      .then((items) => {
        if (controller.signal.aborted) {
          return;
        }
        setServiceItems(items);
        setServiceLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        setServiceItems(null);
        setServiceError(true);
        setServiceLoading(false);
      });
    return () => controller.abort();
  }, [isDelivery, marketplaceId, branchMarketplaceId]);

  // Resolve the fee for the CURRENT delivery type, tagged with the fulfillment
  // key so validation only trusts a fee resolved for the current selection.
  const serviceFee = useMemo<DeliveryServiceFee | null>(() => {
    if (!isDelivery) {
      return null;
    }
    const key = draft.fulfillment;
    // Terminal "cannot price" states come FIRST so a failed fetch or an absent
    // branch id surfaces a real (blocking) error instead of a perpetual
    // "loading" (the fetch effect no-ops when the ids are null, and the catch
    // leaves serviceItems null — so the null check must not pre-empt these).
    if (
      serviceError ||
      marketplaceId == null ||
      branchMarketplaceId == null
    ) {
      return { key, status: "error", serviceItemId: null, price: null };
    }
    if (serviceLoading || serviceItems === null) {
      return { key, status: "loading", serviceItemId: null, price: null };
    }
    const resolved = resolveServiceFee(
      serviceItems,
      draft.fulfillment as CncOrderDeliveryType
    );
    return {
      key,
      status: resolved.status,
      serviceItemId: resolved.serviceItemId,
      price: resolved.price,
    };
  }, [
    isDelivery,
    draft.fulfillment,
    marketplaceId,
    branchMarketplaceId,
    serviceLoading,
    serviceError,
    serviceItems,
  ]);

  // Mirror the resolved fee into the shared draft so the route-independent
  // lifecycle submit() (validation + payload) can read it. Guarded to avoid a
  // render loop: only write when a field actually changed.
  useEffect(() => {
    if (!isDelivery) {
      if (draft.deliveryServiceFee) {
        updateDraft({ deliveryServiceFee: null });
      }
      return;
    }
    if (!serviceFee) {
      return;
    }
    const cur = draft.deliveryServiceFee;
    if (
      !cur ||
      cur.key !== serviceFee.key ||
      cur.status !== serviceFee.status ||
      cur.serviceItemId !== serviceFee.serviceItemId ||
      cur.price !== serviceFee.price
    ) {
      updateDraft({ deliveryServiceFee: serviceFee });
    }
  }, [isDelivery, serviceFee, draft.deliveryServiceFee, updateDraft]);

  const feeResolved = serviceFee?.status === "ok";
  const fee = feeResolved && typeof serviceFee?.price === "number" ? serviceFee.price : 0;
  const loyalty = prefill?.user.userLoyaltyPoints ?? 0;
  // Loyalty payment is offered only once the fee is resolved AND the balance
  // covers it; until then (or if the fee cannot be resolved) it is disabled and
  // the auto-switch effect below forces the selection back to CASH.
  const loyaltyDisabled = !feeResolved || loyalty < fee;

  // Local pre-check state (never persisted to the draft/payload).
  const [checking, setChecking] = useState(false);
  const [zoneOk, setZoneOk] = useState<boolean | null>(null);
  const [slotsResponse, setSlotsResponse] =
    useState<DeliveryTimeSlotsResponse | null>(null);
  // null | "role" | "missing-marketplace"
  const [checkError, setCheckError] = useState<string | null>(null);

  // Currently-resolved delivery coordinates (SAVED selected address or the
  // Places-created NEW address). null => nothing to pre-check yet.
  const resolvedCoords = useMemo<{ lat: number; lng: number } | null>(() => {
    if (draft.fulfillment === "PICKUP") {
      return null;
    }
    if (draft.addressMode === "SAVED") {
      const found = prefill?.addresses.find(
        (a) => a.id === draft.selectedAddressId
      );
      if (found && found.latitude && found.longitude) {
        return { lat: found.latitude, lng: found.longitude };
      }
      return null;
    }
    const a = draft.newAddress;
    if (a && a.latitude && a.longitude) {
      return { lat: a.latitude, lng: a.longitude };
    }
    return null;
  }, [
    draft.fulfillment,
    draft.addressMode,
    draft.selectedAddressId,
    draft.newAddress,
    prefill,
  ]);

  // Selected saved address lacking coordinates -> delivery is impossible.
  const savedAddressMissingCoords = useMemo(() => {
    if (draft.fulfillment === "PICKUP" || draft.addressMode !== "SAVED") {
      return false;
    }
    if (draft.selectedAddressId == null) {
      return false;
    }
    const found = prefill?.addresses.find(
      (a) => a.id === draft.selectedAddressId
    );
    return !!found && (!found.latitude || !found.longitude);
  }, [
    draft.fulfillment,
    draft.addressMode,
    draft.selectedAddressId,
    prefill,
  ]);

  // A previously-chosen standard-delivery time slot is valid only for the address
  // it was picked for (slots are fetched per delivery location). Whenever the
  // delivery ADDRESS (saved id / new-address coordinates) or the address MODE
  // changes, drop the stale slot: the user must re-pick from the freshly fetched
  // slots, and submission stays blocked until they do. Keyed on coordinates (not
  // object identity) so editing apartment/floor keeps a valid slot, and a preserved
  // draft's slot survives closing/reopening the form (identity unchanged on remount).
  const addressIdentity = useMemo(() => {
    if (draft.addressMode === "SAVED") {
      return `SAVED:${draft.selectedAddressId ?? ""}`;
    }
    const a = draft.newAddress;
    return `NEW:${a ? `${a.latitude},${a.longitude}` : ""}`;
  }, [draft.addressMode, draft.selectedAddressId, draft.newAddress]);

  const prevAddressIdentityRef = useRef(addressIdentity);
  useEffect(() => {
    if (prevAddressIdentityRef.current === addressIdentity) {
      return;
    }
    prevAddressIdentityRef.current = addressIdentity;
    if (draft.deliveryTimeSlot) {
      updateDraft({ deliveryTimeSlot: null });
    }
  }, [addressIdentity, draft.deliveryTimeSlot, updateDraft]);

  // Robustness: if the latest slot options no longer include the currently
  // selected slot (e.g. a re-fetch returned a different set for the same
  // address), drop the selection so a slot that isn't actually offered can never
  // be submitted.
  useEffect(() => {
    const selected = draft.deliveryTimeSlot;
    if (!slotsResponse || !selected) {
      return;
    }
    const stillOffered = Object.values(
      slotsResponse.standardDeliveryTimeSlots
    ).some((group) =>
      group.some((s) => s.start === selected.start && s.end === selected.end)
    );
    if (!stillOffered) {
      updateDraft({ deliveryTimeSlot: null });
    }
  }, [slotsResponse, draft.deliveryTimeSlot, updateDraft]);

  // Fire the delivery pre-checks whenever coordinates / fulfillment change.
  // Catches ALL errors (esp. the expected 403) so nothing throws or logs out.
  useEffect(() => {
    if (draft.fulfillment === "PICKUP") {
      setChecking(false);
      setZoneOk(null);
      setSlotsResponse(null);
      setCheckError(null);
      updateDraft({ deliveryZoneCheck: null });
      return;
    }
    if (!resolvedCoords) {
      setChecking(false);
      setZoneOk(null);
      setSlotsResponse(null);
      setCheckError(null);
      updateDraft({ deliveryZoneCheck: null });
      return;
    }

    // Key for THIS exact selection (type + coords). Every result written below is
    // tagged with it so validation can reject a result once the selection changes.
    const zoneKey = deliveryZoneKey(
      draft.fulfillment,
      resolvedCoords.lat,
      resolvedCoords.lng
    );

    if (marketplaceLat == null || marketplaceLng == null) {
      setChecking(false);
      setZoneOk(null);
      setSlotsResponse(null);
      setCheckError("missing-marketplace");
      // Cannot verify without marketplace coords -> UNAVAILABLE (degrade).
      updateDraft({ deliveryZoneCheck: { key: zoneKey, inZone: null } });
      return;
    }

    const controller = new AbortController();
    const deliveryType = draft.fulfillment as CncOrderDeliveryType;
    const { lat, lng } = resolvedCoords;

    setChecking(true);
    setCheckError(null);
    // Drop the previous address's slot options at the START of a new check so the
    // selector cannot offer a stale (address-A) slot while address B is pending.
    setSlotsResponse(null);

    const run = async () => {
      try {
        const inZone = await checkDeliveryDistance(
          { lat, lng, marketplaceLat, marketplaceLng, deliveryType },
          controller.signal
        );
        let slots: DeliveryTimeSlotsResponse | null = null;
        if (deliveryType === "STANDARD_DELIVERY") {
          slots = await checkDeliveryTimeSlots(
            { lat, lng, marketplaceLat, marketplaceLng },
            controller.signal
          );
        }
        if (controller.signal.aborted) {
          return;
        }
        setZoneOk(inZone);
        setSlotsResponse(slots);
        setChecking(false);
        updateDraft({ deliveryZoneCheck: { key: zoneKey, inZone } });
      } catch {
        if (controller.signal.aborted) {
          return;
        }
        // Expected VC-2 403 (or any pre-check failure): degrade gracefully.
        // Record UNAVAILABLE (inZone null) for THIS selection so submit falls
        // back to the backend rather than hard-blocking on an unverifiable check.
        setZoneOk(null);
        setSlotsResponse(null);
        setCheckError("role");
        setChecking(false);
        updateDraft({ deliveryZoneCheck: { key: zoneKey, inZone: null } });
      }
    };

    run();
    return () => controller.abort();
  }, [
    draft.fulfillment,
    resolvedCoords,
    marketplaceLat,
    marketplaceLng,
    updateDraft,
  ]);

  // If loyalty can no longer cover the current fee, force payment back to CASH.
  useEffect(() => {
    if (draft.paymentType === "LOYALTY_POINTS" && loyalty < fee) {
      updateDraft({ paymentType: "CASH" });
    }
  }, [draft.paymentType, loyalty, fee, updateDraft]);

  const handleFulfillment = (next: ConsultOrderFulfillment) => {
    if (next === draft.fulfillment) {
      return;
    }
    if (next === "FAST_DELIVERY") {
      // No time slots for fast delivery.
      updateDraft({ fulfillment: next, deliveryTimeSlot: null });
    } else {
      updateDraft({ fulfillment: next });
    }
  };

  const handleAddressMode = (next: ConsultOrderAddressMode) => {
    if (next === draft.addressMode) {
      return;
    }
    if (next === "NEW") {
      // Switching away from SAVED clears the saved selection.
      updateDraft({ addressMode: "NEW", selectedAddressId: null });
    } else {
      // Switching away from NEW clears the Places address + save flag.
      updateDraft({
        addressMode: "SAVED",
        newAddress: null,
        saveNewAddress: false,
      });
    }
  };

  const currentSlotValue = draft.deliveryTimeSlot
    ? slotOptionValue(draft.deliveryTimeSlot.start, draft.deliveryTimeSlot.end)
    : "";

  // Live (non-submit) status for the resolved delivery fee. `ok` shows nothing
  // here (the amount is rendered in the payment legend); every other state is a
  // reason the order cannot be priced/sent.
  const serviceFeeMessage: string | null = !serviceFee
    ? null
    : serviceFee.status === "loading"
    ? "Učitavanje cene dostave…"
    : serviceFee.status === "missing"
    ? "Cena dostave nije podešena za ovu radnju."
    : serviceFee.status === "duplicate"
    ? "Cena dostave je dvosmislena (više stavki usluge) — obratite se podršci."
    : serviceFee.status === "malformed"
    ? "Cena dostave nije ispravno podešena za izabrani tip."
    : serviceFee.status === "error"
    ? "Greška pri učitavanju cene dostave."
    : null;

  return (
    <div className={styles.DeliverySection}>
      {/* Fulfillment */}
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Način preuzimanja</legend>
        <div className={styles.segmented} role="radiogroup">
          {FULFILLMENT_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={styles.segment}
              data-active={draft.fulfillment === opt.value}
            >
              <input
                type="radio"
                name="consult-fulfillment"
                value={opt.value}
                checked={draft.fulfillment === opt.value}
                onChange={() => handleFulfillment(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {isDelivery && (
        <>
          {/* Address mode */}
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Adresa dostave</legend>
            <div className={styles.segmented} role="radiogroup">
              <label
                className={styles.segment}
                data-active={draft.addressMode === "SAVED"}
              >
                <input
                  type="radio"
                  name="consult-address-mode"
                  value="SAVED"
                  checked={draft.addressMode === "SAVED"}
                  onChange={() => handleAddressMode("SAVED")}
                />
                <span>Sačuvana adresa</span>
              </label>
              <label
                className={styles.segment}
                data-active={draft.addressMode === "NEW"}
              >
                <input
                  type="radio"
                  name="consult-address-mode"
                  value="NEW"
                  checked={draft.addressMode === "NEW"}
                  onChange={() => handleAddressMode("NEW")}
                />
                <span>Nova adresa</span>
              </label>
            </div>

            {draft.addressMode === "SAVED" ? (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="consult-saved-address">
                  Izaberite adresu
                </label>
                <select
                  id="consult-saved-address"
                  className={styles.select}
                  data-consult-field="address"
                  value={draft.selectedAddressId ?? ""}
                  onChange={(e) =>
                    updateDraft({
                      selectedAddressId: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                >
                  <option value="">— izaberite —</option>
                  {(prefill?.addresses ?? []).map((addr) => (
                    <option key={addr.id} value={addr.id}>
                      {[addr.name, addr.number, addr.city]
                        .filter(Boolean)
                        .join(", ")}
                    </option>
                  ))}
                </select>
                {savedAddressMissingCoords && (
                  <p className={styles.warning} role="alert">
                    Izabrana adresa nema koordinate — nije moguća dostava.
                  </p>
                )}
              </div>
            ) : (
              <div
                className={styles.field}
                data-consult-field="address"
                tabIndex={-1}
              >
                <AddressCreate />
              </div>
            )}

            {errors?.address && (
              <p className={styles.error} role="alert">
                {errors.address}
              </p>
            )}
          </fieldset>

          {/* Pre-check status */}
          <div className={styles.checkStatus} aria-live="polite">
            {checking && <span>Provera zone i termina dostave…</span>}
            {checkError === "missing-marketplace" && (
              <span className={styles.warning}>
                Koordinate radnje nisu dostupne — nije moguće proveriti
                zonu/termine dostave.
              </span>
            )}
            {checkError === "role" && (
              <span className={styles.warning}>
                Nije moguće proveriti zonu/termine za ovu ulogu (potrebna backend
                dozvola).
              </span>
            )}
            {!checking && checkError == null && zoneOk === false && (
              <span className={styles.warning}>
                Adresa je van zone dostave za izabrani tip.
              </span>
            )}
          </div>

          {/* Delivery-fee status + focus target for a submit-time delivery error. */}
          <div
            className={styles.checkStatus}
            data-consult-field="delivery"
            tabIndex={-1}
            aria-live="polite"
          >
            {serviceFeeMessage && (
              <span
                className={
                  serviceFee?.status === "loading" ? undefined : styles.warning
                }
              >
                {serviceFeeMessage}
              </span>
            )}
            {errors?.delivery && (
              <p className={styles.error} role="alert">
                {errors.delivery}
              </p>
            )}
          </div>

          {/* Standard delivery time slots */}
          {draft.fulfillment === "STANDARD_DELIVERY" && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="consult-time-slot">
                Termin dostave
              </label>
              <select
                id="consult-time-slot"
                className={styles.select}
                data-consult-field="deliveryTimeSlot"
                value={currentSlotValue}
                disabled={checking || !slotsResponse}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) {
                    updateDraft({ deliveryTimeSlot: null });
                    return;
                  }
                  const [start, end] = raw.split("|");
                  updateDraft({ deliveryTimeSlot: { start, end } });
                }}
              >
                <option value="">— izaberite termin —</option>
                {slotsResponse &&
                  Object.entries(slotsResponse.standardDeliveryTimeSlots).map(
                    ([group, slots]) => (
                      <optgroup key={group} label={group}>
                        {slots.map((slot) => (
                          <option
                            key={slotOptionValue(slot.start, slot.end)}
                            value={slotOptionValue(slot.start, slot.end)}
                          >
                            {slot.hoursSlot || `${slot.start} - ${slot.end}`}
                          </option>
                        ))}
                      </optgroup>
                    )
                  )}
              </select>
              {errors?.deliveryTimeSlot && (
                <p className={styles.error} role="alert">
                  {errors.deliveryTimeSlot}
                </p>
              )}
            </div>
          )}

          {/* Payment */}
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>
              Način plaćanja dostave
              <span className={styles.fee}>
                Naknada za dostavu:{" "}
                {feeResolved
                  ? draft.paymentType === "LOYALTY_POINTS"
                    ? // Loyalty fee is charged in points, not dinars.
                      `${fee}`
                    : `${fee} RSD`
                  : "—"}
              </span>
            </legend>
            <div className={styles.radioRow}>
              <label className={styles.radio}>
                <input
                  type="radio"
                  name="consult-payment"
                  value="CASH"
                  checked={draft.paymentType === "CASH"}
                  onChange={() => updateDraft({ paymentType: "CASH" })}
                />
                <span>Gotovina</span>
              </label>
              <label className={styles.radio} data-disabled={loyaltyDisabled}>
                <input
                  type="radio"
                  name="consult-payment"
                  value="LOYALTY_POINTS"
                  checked={draft.paymentType === "LOYALTY_POINTS"}
                  disabled={loyaltyDisabled}
                  onChange={() =>
                    updateDraft({ paymentType: "LOYALTY_POINTS" })
                  }
                />
                <span>Loyalty poeni ({loyalty})</span>
              </label>
            </div>
          </fieldset>

          {/* Personal ID */}
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>
              Lični identifikacioni podaci
            </legend>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label
                  className={styles.label}
                  htmlFor="consult-personal-id-type"
                >
                  Tip dokumenta
                </label>
                <select
                  id="consult-personal-id-type"
                  className={styles.select}
                  value={draft.personalIdType}
                  onChange={(e) =>
                    updateDraft({
                      personalIdType: e.target.value as PersonalIdType,
                    })
                  }
                >
                  {PERSONAL_ID_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="consult-personal-id">
                  Broj dokumenta
                </label>
                <input
                  id="consult-personal-id"
                  className={styles.input}
                  data-consult-field="personalId"
                  type="text"
                  value={draft.personalId}
                  aria-invalid={!!errors?.personalId}
                  aria-describedby={
                    errors?.personalId ? "consult-personal-id-error" : undefined
                  }
                  onChange={(e) =>
                    updateDraft({ personalId: e.target.value })
                  }
                />
              </div>
            </div>
            {errors?.personalId && (
              <p
                id="consult-personal-id-error"
                className={styles.error}
                role="alert"
              >
                {errors.personalId}
              </p>
            )}
          </fieldset>

          {/* Delivery note */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="consult-note-for-delivery">
              Napomena za dostavu (opciono)
            </label>
            <textarea
              id="consult-note-for-delivery"
              className={styles.textarea}
              rows={2}
              value={draft.noteForDelivery}
              onChange={(e) =>
                updateDraft({ noteForDelivery: e.target.value })
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DeliverySection;
